import { startRegistration } from '@simplewebauthn/browser';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import TextFieldInput from '@/components/atoms/Form/TextFieldInput';
import {
    useCreateWebauthnCredentialForUserMutation,
    useDeleteWebauthnCredentialForUserMutation,
    useWebauthnSetupOptionsForUserMutation,
} from '@/services/innovorder/auth/auth.endpoints';
import type { WebauthnCredentialListItem } from '@/services/innovorder/auth/auth.type';

import { BackupCodeItem, BackupCodesList } from '@/pages/Mfa/Mfa.style';

import {
    Actions,
    CredentialDetails,
    CredentialList,
    CredentialMeta,
    CredentialName,
    CredentialRow,
    EmptyState,
    ErrorText,
    Modal,
    ModalCard,
    Section,
    SectionDescription,
    StyledButton,
} from '../Security.style';

type Props = {
    userId: number;
    token: string;
    credentials: WebauthnCredentialListItem[];
};

const formatDate = (raw: string | null): string => {
    if (!raw) return '—';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString();
};

const PasskeySection = ({ userId, token, credentials }: Props): ReactElement => {
    const intl = useIntl();
    const webauthnSupported = typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined';

    const [getOptions, { data: optionsData, error: optionsError }] = useWebauthnSetupOptionsForUserMutation();
    const [createCredential, { data: createdData, error: createError, isLoading: creating, reset: resetCreate }] =
        useCreateWebauthnCredentialForUserMutation();
    const [deleteCredential] = useDeleteWebauthnCredentialForUserMutation();

    const [ceremonyError, setCeremonyError] = useState<string | null>(null);
    const [shownBackupCodes, setShownBackupCodes] = useState<string[] | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [deviceName, setDeviceName] = useState('');

    // Drive ceremony when options arrive.
    useEffect(() => {
        if (!optionsData?.options || !optionsData.registrationId) return;
        let cancelled = false;
        const run = async (): Promise<void> => {
            try {
                const attestation = await startRegistration({ optionsJSON: optionsData.options });
                if (cancelled) return;
                createCredential({
                    token,
                    userId,
                    attestation,
                    deviceName: deviceName.trim() || undefined,
                    registrationId: optionsData.registrationId,
                });
            } catch (err) {
                if (cancelled) return;
                setCeremonyError(err instanceof Error ? err.message : 'webauthn_ceremony_failed');
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [optionsData, createCredential, token, userId, deviceName]);

    // Close the modal once the create call succeeds; surface backup codes if the API attached them
    // (only on the very first MFA enrolment).
    useEffect(() => {
        if (!createdData) return;
        setModalOpen(false);
        setDeviceName('');
        if (createdData.backupCodes && createdData.backupCodes.length > 0) {
            setShownBackupCodes(createdData.backupCodes);
        }
    }, [createdData]);

    const handleOpenModal = useCallback(() => {
        setCeremonyError(null);
        resetCreate();
        setDeviceName('');
        setModalOpen(true);
    }, [resetCreate]);

    const handleRegister = useCallback(() => {
        setCeremonyError(null);
        getOptions({ token, userId });
    }, [getOptions, token, userId]);

    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
        setDeviceName('');
    }, []);

    const handleRemove = useCallback(
        async (passkeyId: number) => {
            const confirmed = window.confirm(intl.formatMessage({ id: 'security.passkeys.confirmRemove' }));
            if (!confirmed) return;
            await deleteCredential({ token, userId, passkeyId });
        },
        [deleteCredential, intl, token, userId],
    );

    const errorLabel = ceremonyError
        ? intl.formatMessage({ id: 'security.passkeys.ceremonyFailed' })
        : optionsError || createError
        ? intl.formatMessage({ id: 'security.passkeys.requestFailed' })
        : null;

    return (
        <Section>
            <h2>
                <FormattedMessage id="security.passkeys.title" />
            </h2>
            <SectionDescription>
                <FormattedMessage id="security.passkeys.subtitle" />
            </SectionDescription>

            {credentials.length === 0 ? (
                <EmptyState>
                    <FormattedMessage id="security.passkeys.empty" />
                </EmptyState>
            ) : (
                <CredentialList>
                    {credentials.map((c) => (
                        <CredentialRow key={c.userPasskeyId}>
                            <CredentialMeta>
                                <CredentialName>
                                    {c.deviceName || intl.formatMessage({ id: 'security.passkeys.unnamedDevice' })}
                                </CredentialName>
                                <CredentialDetails>
                                    <FormattedMessage
                                        id="security.passkeys.lastUsed"
                                        values={{ date: formatDate(c.lastUsedAt) }}
                                    />
                                </CredentialDetails>
                                <CredentialDetails>
                                    <FormattedMessage
                                        id="security.passkeys.added"
                                        values={{ date: formatDate(c.createdAt) }}
                                    />
                                </CredentialDetails>
                            </CredentialMeta>
                            <StyledButton
                                buttonType="secondary"
                                onClick={(): Promise<void> => handleRemove(c.userPasskeyId)}
                            >
                                <FormattedMessage id="security.actions.remove" />
                            </StyledButton>
                        </CredentialRow>
                    ))}
                </CredentialList>
            )}

            {!modalOpen && errorLabel ? <ErrorText>{errorLabel}</ErrorText> : null}

            {webauthnSupported ? (
                <Actions>
                    <StyledButton buttonType="primary" onClick={handleOpenModal}>
                        <FormattedMessage id="security.passkeys.add" />
                    </StyledButton>
                </Actions>
            ) : null}

            {modalOpen ? (
                <Modal role="dialog" aria-modal="true">
                    <ModalCard>
                        <h2>
                            <FormattedMessage id="mfa.setup.webauthn.title" />
                        </h2>
                        <SectionDescription>
                            <FormattedMessage id="mfa.setup.webauthn.subtitle" />
                        </SectionDescription>
                        <TextFieldInput
                            placeholder={intl.formatMessage({ id: 'mfa.setup.webauthn.deviceNamePlaceholder' })}
                            value={deviceName}
                            onChange={(e): void => setDeviceName(e.currentTarget.value)}
                        />
                        {errorLabel ? <ErrorText>{errorLabel}</ErrorText> : null}
                        <Actions>
                            <StyledButton buttonType="primary" disabled={creating} onClick={handleRegister}>
                                <FormattedMessage id="mfa.setup.webauthn.register" />
                            </StyledButton>
                            <StyledButton buttonType="secondary" onClick={handleCloseModal}>
                                <FormattedMessage id="security.actions.cancel" />
                            </StyledButton>
                        </Actions>
                    </ModalCard>
                </Modal>
            ) : null}

            {shownBackupCodes ? (
                <Modal role="dialog" aria-modal="true">
                    <ModalCard>
                        <h2>
                            <FormattedMessage id="mfa.setup.backupTitle" />
                        </h2>
                        <p>
                            <FormattedMessage id="mfa.setup.backupSubtitle" />
                        </p>
                        <BackupCodesList>
                            {shownBackupCodes.map((code) => (
                                <BackupCodeItem key={code}>{code}</BackupCodeItem>
                            ))}
                        </BackupCodesList>
                        <StyledButton buttonType="primary" onClick={(): void => setShownBackupCodes(null)}>
                            <FormattedMessage id="mfa.setup.backupAcknowledge" />
                        </StyledButton>
                    </ModalCard>
                </Modal>
            ) : null}
        </Section>
    );
};

export default PasskeySection;
